import React, { useEffect, useMemo, useRef, useState } from 'react';
import * as Sentry from '@sentry/react';
import moment, { Moment } from 'moment-timezone';
import 'react-dates/initialize';
import { SingleDatePicker } from 'react-dates';
import { ConversationThreadPanel } from './ConversationThreadPanel';
import { SavedSearches } from './SavedSearches';
import BEM from 'common/bem';
import AuditLog from 'common/components/AuditLog/AuditLog';
import BasicModal from 'common/components/BasicModal';
import ExportHistory from 'common/components/ExportHistory/ExportHistory';
import mobxInjectSelect from 'common/utils/mobxInjectSelect';
import type {
  ArchivingAuditLogListRequest,
  ArchivingAuditLogListResponse,
  ArchivingConversationMetadataResponse,
  ArchivingConversationResponse,
  ArchivingDeleteSavedSearchResponse,
  ArchivingExportResponse,
  ArchivingExportSummary,
  ArchivingFilterNode,
  ArchivingMessageSearchResponse,
  ArchivingSavedSearchFilters,
  ArchivingSavedSearchListResponse,
  ArchivingSavedSearchResponse,
  ArchivingTypeaheadResult,
} from 'js-sdk/src/apis/ArchivingAPI';

const classes = BEM.with('MessageSearch');

type ArchivedMessageResult = {
  messageId?: string;
  sentAt?: string;
  senderAccountId?: string | null;
  senderDisplayName?: string;
  recipientAccountId?: string | null;
  recipientDisplayName?: string | null;
  body?: string;
  hasAttachment?: boolean;
  /** Raw MESSAGES.MESSAGETYPE value (e.g. P2P, GROUP, DIST, PUBLIC_GROUP, DIST_GROUP), or null when unset. */
  messageType?: string | null;
  conversationId?: string;
  channelId?: string | null;
};

const TIME_RANGE_PRESETS = [
  { value: 'last24h', label: 'Last 24 hours' },
  { value: 'last7d', label: 'Last 7 days' },
  { value: 'last30d', label: 'Last 30 days' },
  { value: 'thisQuarter', label: 'This quarter' },
  { value: 'custom', label: 'Custom range' },
] as const;

type TimeRangePreset = (typeof TIME_RANGE_PRESETS)[number]['value'];

const MESSAGE_TYPE_OPTIONS = [
  { value: 'P2P', label: 'P2P' },
  { value: 'GROUP', label: 'Group' },
  { value: 'DIST', label: 'Broadcast' },
  { value: 'PUBLIC_GROUP', label: 'Forum' },
  { value: 'DIST_GROUP', label: 'Group (SMS)' },
] as const;

/** How a User/Role row's chosen id must relate to a matching message. */
type UserRoleMatchRole = 'sender' | 'recipient' | 'either';

/** A single repeatable User/Role search row. Unselected rows (no `selected`) are ignored on submit. */
type UserRoleRow = {
  /** Stable React key — never sent to the server. */
  id: string;
  inputValue: string;
  selected: ArchivingTypeaheadResult | null;
  role: UserRoleMatchRole;
  suggestions: ArchivingTypeaheadResult[];
  isLoadingSuggestions: boolean;
  suggestionsError: boolean;
  /** Keyboard-highlighted suggestion index, or -1 when none is highlighted. */
  activeSuggestionIndex: number;
};

function createEmptyRow(id: string): UserRoleRow {
  return {
    id,
    inputValue: '',
    selected: null,
    role: 'either',
    suggestions: [],
    isLoadingSuggestions: false,
    suggestionsError: false,
    activeSuggestionIndex: -1,
  };
}

/**
 * Builds the AND/OR filter tree sent to analytics-service. Selected User/Role rows are combined
 * with each other using matchMode (OR for "Any", AND for "All") — this is the only thing matchMode
 * controls; messageType and the attachment toggle are always ANDed in alongside that group,
 * regardless of matchMode, matching the product decision that "Match Any/All" scopes only to the
 * User/Role rows. A row set to "either" OR's the sender and recipient columns for that one id,
 * since the same id (user or role) could appear as either.
 */
function buildUserRoleClause(row: { id: string; role: UserRoleMatchRole }): ArchivingFilterNode {
  const senderLeaf: ArchivingFilterNode = {
    field: 'senderAccountId',
    operator: 'EQ',
    values: [row.id],
  };
  const recipientLeaf: ArchivingFilterNode = {
    field: 'recipientAccountId',
    operator: 'EQ',
    values: [row.id],
  };
  if (row.role === 'sender') return senderLeaf;
  if (row.role === 'recipient') return recipientLeaf;
  return { op: 'OR', clauses: [senderLeaf, recipientLeaf] };
}

function buildFilterTree(
  userRoleRows: Array<{ id: string; role: UserRoleMatchRole }>,
  matchMode: 'any' | 'all',
  messageType: string,
  hasAttachmentOnly: boolean
): ArchivingFilterNode | undefined {
  const clauses: ArchivingFilterNode[] = [];

  const rowClauses = userRoleRows.map(buildUserRoleClause);
  if (rowClauses.length === 1) {
    clauses.push(rowClauses[0]);
  } else if (rowClauses.length > 1) {
    clauses.push({ op: matchMode === 'any' ? 'OR' : 'AND', clauses: rowClauses });
  }

  if (messageType) {
    clauses.push({ field: 'messageType', operator: 'EQ', values: [messageType] });
  }
  if (hasAttachmentOnly) {
    clauses.push({ field: 'hasAttachment', operator: 'EQ', values: ['true'] });
  }

  if (clauses.length === 0) return undefined;
  if (clauses.length === 1) return clauses[0];
  return { op: 'AND', clauses };
}

/**
 * Computes the {startDate, endDate} ISO-8601 bounds for a time range preset. For "custom", bounds
 * are only returned once both endpoints are picked — an incomplete custom range applies no date
 * filter rather than blocking search, since search has no required fields.
 */
function computeDateRange(
  preset: TimeRangePreset,
  customStartDate: Moment | null,
  customEndDate: Moment | null
): { startDate?: string; endDate?: string } {
  const now = moment();
  switch (preset) {
    case 'last24h':
      return {
        startDate: moment(now).subtract(24, 'hours').toISOString(),
        endDate: now.toISOString(),
      };
    case 'last7d':
      return {
        startDate: moment(now).subtract(7, 'days').toISOString(),
        endDate: now.toISOString(),
      };
    case 'last30d':
      return {
        startDate: moment(now).subtract(30, 'days').toISOString(),
        endDate: now.toISOString(),
      };
    case 'thisQuarter':
      return {
        startDate: moment(now).startOf('quarter').toISOString(),
        endDate: now.toISOString(),
      };
    case 'custom':
      if (!customStartDate || !customEndDate) return {};
      return {
        startDate: moment(customStartDate).startOf('day').toISOString(),
        endDate: moment(customEndDate).endOf('day').toISOString(),
      };
    default:
      return {};
  }
}

type MessageSearchProps = {};

type MobxProps = {
  client: {
    archiving: {
      searchMessages: (
        organizationId: string,
        query: string,
        options?: {
          startDate?: string;
          endDate?: string;
          hasAttachment?: boolean;
          senderAccountIds?: string[];
          recipientAccountIds?: string[];
          filter?: ArchivingFilterNode;
          limit?: number;
        }
      ) => Promise<ArchivingMessageSearchResponse>;
      getConversation: (
        organizationId: string,
        conversationId: string,
        options?: { limit?: number }
      ) => Promise<ArchivingConversationResponse>;
      getConversationMetadata: (
        organizationId: string,
        conversationId: string
      ) => Promise<ArchivingConversationMetadataResponse>;
      startExport: (
        organizationId: string,
        options: {
          exportName?: string;
          messageIds: string[];
          includeAttachments?: boolean;
          notes?: string;
        }
      ) => Promise<ArchivingExportResponse>;
      listExports: (
        organizationId: string
      ) => Promise<{ data: { exports: ArchivingExportSummary[] } }>;
      getExportDownloadUrl: (
        organizationId: string,
        jobId: string
      ) => Promise<{ data: { downloadUrl: string } }>;
      searchUsersAndRoles: (
        organizationId: string,
        query: string,
        options?: { limit?: number }
      ) => Promise<{ data: { results: ArchivingTypeaheadResult[] } }>;
      createSavedSearch: (
        organizationId: string,
        name: string,
        filters: ArchivingSavedSearchFilters
      ) => Promise<ArchivingSavedSearchResponse>;
      getSavedSearches: (
        organizationId: string,
        options?: { mine?: boolean }
      ) => Promise<ArchivingSavedSearchListResponse>;
      deleteSavedSearch: (
        organizationId: string,
        searchId: string
      ) => Promise<ArchivingDeleteSavedSearchResponse>;
      getAuditLog: (
        organizationId: string,
        options?: Omit<ArchivingAuditLogListRequest, 'orgId'>
      ) => Promise<ArchivingAuditLogListResponse>;
    };
    currentUserId: string;
  } | null;
  currentOrganizationId: string;
  /** Whether to show the Audit Log tab. UI only — analytics-service enforces access itself. */
  canViewArchivingAuditLog?: boolean;
};

type CombinedProps = MessageSearchProps & MobxProps;

export function MessageSearchInner({
  client,
  currentOrganizationId,
  canViewArchivingAuditLog = false,
}: CombinedProps) {
  const [query, setQuery] = useState('');
  // Per-instance counter (not module-level) so row ids are deterministic (row-1, row-2, …) for
  // every fresh mount of this component, including across tests in the same file.
  const nextRowId = useRef(0);
  function createRow(): UserRoleRow {
    nextRowId.current += 1;
    return createEmptyRow(`row-${nextRowId.current}`);
  }
  const [userRoleRows, setUserRoleRows] = useState<UserRoleRow[]>(() => [createRow()]);
  const [userRoleMatchMode, setUserRoleMatchMode] = useState<'any' | 'all'>('all');
  const [messageType, setMessageType] = useState('');
  const [hasAttachmentOnly, setHasAttachmentOnly] = useState(false);
  const [timeRangePreset, setTimeRangePreset] = useState<TimeRangePreset>('last24h');
  const [customStartDate, setCustomStartDate] = useState<Moment | null>(null);
  const [customEndDate, setCustomEndDate] = useState<Moment | null>(null);
  const [customStartFocused, setCustomStartFocused] = useState(false);
  const [customEndFocused, setCustomEndFocused] = useState(false);
  const [results, setResults] = useState<ArchivedMessageResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasSearched, setHasSearched] = useState(false);
  const [selectedMessages, setSelectedMessages] = useState<Map<string, ArchivedMessageResult>>(
    new Map()
  );
  const selectedMessageIds = useMemo(() => new Set(selectedMessages.keys()), [selectedMessages]);
  const [openThread, setOpenThread] = useState<{
    conversationId: string;
    messageId?: string;
  } | null>(null);
  const [activeTab, setActiveTab] = useState<
    'search' | 'exportBuilder' | 'exportHistory' | 'savedSearches' | 'auditLog'
  >('search');
  const [exportName, setExportName] = useState('');
  const [includeAttachments, setIncludeAttachments] = useState(false);
  const [exportNotes, setExportNotes] = useState('');
  const [isExporting, setIsExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const [exportJobId, setExportJobId] = useState<string | null>(null);
  const [addingThreadId, setAddingThreadId] = useState<string | null>(null);
  const [addThreadError, setAddThreadError] = useState<string | null>(null);
  const [isSaveSearchModalOpen, setIsSaveSearchModalOpen] = useState(false);
  const [saveSearchName, setSaveSearchName] = useState('');
  const [saveSearchError, setSaveSearchError] = useState<string | null>(null);

  // Bumped on every new search (submit, clear, or run-saved-search) so a slower/earlier
  // executeSearch's response can't overwrite results/hasSearched after a newer search — or a
  // clear — has already superseded it.
  const searchSeq = useRef(0);
  const debounceTimers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  // Tracks the latest lookup requested per row. Clearing a pending debounce timer only stops a
  // request that hasn't fired yet — it can't cancel one already in flight. Without this guard, a
  // slower/earlier response arriving after a newer one would overwrite the newer (correct)
  // suggestions with stale ones, letting the user pick a suggestion for text they've since changed.
  const requestSeq = useRef<Map<string, number>>(new Map());
  useEffect(() => {
    const timers = debounceTimers.current;
    return () => {
      timers.forEach((timer) => clearTimeout(timer));
    };
  }, []);

  function updateRow(rowId: string, updates: Partial<UserRoleRow>) {
    setUserRoleRows((previous) =>
      previous.map((row) => (row.id === rowId ? { ...row, ...updates } : row))
    );
  }

  function onRowInputChange(rowId: string, value: string) {
    // isLoadingSuggestions is reset here too — otherwise clearing the input (or shrinking it back
    // below the 2-character minimum) while a lookup is in flight leaves the spinner on forever: the
    // bump to requestSeq below makes that in-flight response's own seq check skip its update, so
    // nothing else would ever flip isLoadingSuggestions back to false.
    updateRow(rowId, {
      inputValue: value,
      selected: null,
      suggestions: [],
      suggestionsError: false,
      isLoadingSuggestions: false,
      activeSuggestionIndex: -1,
    });

    const existingTimer = debounceTimers.current.get(rowId);
    if (existingTimer) clearTimeout(existingTimer);

    const mySeq = (requestSeq.current.get(rowId) || 0) + 1;
    requestSeq.current.set(rowId, mySeq);

    if (!client || value.trim().length < 2) return;

    const timer = setTimeout(async () => {
      updateRow(rowId, { isLoadingSuggestions: true });
      try {
        const response = await client.archiving.searchUsersAndRoles(
          currentOrganizationId,
          value.trim(),
          { limit: 8 }
        );
        if (requestSeq.current.get(rowId) !== mySeq) return; // superseded by a newer lookup
        updateRow(rowId, {
          suggestions: response?.data?.results || [],
          isLoadingSuggestions: false,
          activeSuggestionIndex: -1,
        });
      } catch (err) {
        if (requestSeq.current.get(rowId) !== mySeq) return;
        updateRow(rowId, { suggestions: [], suggestionsError: true, isLoadingSuggestions: false });
      }
    }, 300);
    debounceTimers.current.set(rowId, timer);
  }

  function onSelectSuggestion(rowId: string, suggestion: ArchivingTypeaheadResult) {
    // Invalidate any in-flight lookup so a late response can't reopen the dropdown or override
    // this selection after the fact.
    const existingTimer = debounceTimers.current.get(rowId);
    if (existingTimer) clearTimeout(existingTimer);
    requestSeq.current.set(rowId, (requestSeq.current.get(rowId) || 0) + 1);

    updateRow(rowId, {
      selected: suggestion,
      inputValue: suggestion.displayName,
      suggestions: [],
      suggestionsError: false,
      activeSuggestionIndex: -1,
      isLoadingSuggestions: false,
    });
  }

  /** Arrow-key/Enter/Escape handling for a user/role row's combobox input. */
  function onRowInputKeyDown(row: UserRoleRow, event: React.KeyboardEvent<HTMLInputElement>) {
    if (row.suggestions.length === 0) return;

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      const next = Math.min(row.activeSuggestionIndex + 1, row.suggestions.length - 1);
      updateRow(row.id, { activeSuggestionIndex: next });
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      const next = Math.max(row.activeSuggestionIndex - 1, 0);
      updateRow(row.id, { activeSuggestionIndex: next });
    } else if (event.key === 'Enter') {
      const active = row.suggestions[row.activeSuggestionIndex];
      if (active) {
        event.preventDefault();
        onSelectSuggestion(row.id, active);
      }
    } else if (event.key === 'Escape') {
      updateRow(row.id, { suggestions: [], activeSuggestionIndex: -1 });
    }
  }

  function onRowRoleChange(rowId: string, role: UserRoleMatchRole) {
    updateRow(rowId, { role });
  }

  function addUserRoleRow() {
    setUserRoleRows((previous) => [...previous, createRow()]);
  }

  function removeUserRoleRow(rowId: string) {
    setUserRoleRows((previous) => {
      const next = previous.filter((row) => row.id !== rowId);
      return next.length > 0 ? next : [createRow()];
    });
    const timer = debounceTimers.current.get(rowId);
    if (timer) {
      clearTimeout(timer);
      debounceTimers.current.delete(rowId);
    }
    requestSeq.current.delete(rowId);
  }

  function toggleSelected(result: ArchivedMessageResult) {
    const messageId = result.messageId;
    if (!messageId) return;
    setSelectedMessages((previous) => {
      const next = new Map(previous);
      if (next.has(messageId)) {
        next.delete(messageId);
      } else {
        next.set(messageId, result);
      }
      return next;
    });
    setExportJobId(null);
  }

  function addToSelection(newResults: ArchivedMessageResult[]) {
    setSelectedMessages((previous) => {
      const next = new Map(previous);
      newResults.forEach((result) => {
        if (result.messageId) next.set(result.messageId, result);
      });
      return next;
    });
    setExportJobId(null);
  }

  function selectAllResults() {
    addToSelection(results);
  }

  function clearSelection() {
    setSelectedMessages(new Map());
    setExportJobId(null);
  }

  async function onAddFullThread(conversationId?: string) {
    if (!client || !conversationId) return;

    setAddingThreadId(conversationId);
    setAddThreadError(null);
    try {
      const response = await client.archiving.getConversation(
        currentOrganizationId,
        conversationId
      );
      addToSelection(response?.data?.messages || []);
    } catch (err) {
      // Its own error state, not the shared search `error` — this action can run while search
      // results are already showing (or a search error is), and clobbering that unrelated state
      // would misattribute this failure to search or silently overwrite a search error still
      // relevant to what's on screen.
      setAddThreadError(
        'Something went wrong adding the full thread to the export. Please try again.'
      );
    } finally {
      setAddingThreadId(null);
    }
  }

  /**
   * The actual search call, taking every filter value as a parameter rather than reading from
   * state directly — this lets onRunSavedSearch fire the search with restored values immediately,
   * without waiting a render for state setters to flush.
   */
  async function executeSearch(params: {
    query: string;
    selectedRows: Array<{ id: string; role: UserRoleMatchRole }>;
    matchMode: 'any' | 'all';
    messageType: string;
    hasAttachmentOnly: boolean;
    timeRangePreset: TimeRangePreset;
    customStartDate: Moment | null;
    customEndDate: Moment | null;
  }) {
    if (!client) return;

    const mySeq = (searchSeq.current += 1);
    setIsLoading(true);
    setError(null);
    try {
      const { startDate, endDate } = computeDateRange(
        params.timeRangePreset,
        params.customStartDate,
        params.customEndDate
      );
      const filter = buildFilterTree(
        params.selectedRows,
        params.matchMode,
        params.messageType,
        params.hasAttachmentOnly
      );
      const response = await client.archiving.searchMessages(currentOrganizationId, params.query, {
        limit: 25,
        ...(startDate && { startDate }),
        ...(endDate && { endDate }),
        ...(filter && { filter }),
      });
      if (searchSeq.current !== mySeq) return; // superseded by a newer search or a clear
      setResults(response?.data?.results || []);
    } catch (err) {
      if (searchSeq.current !== mySeq) return;
      setError('Something went wrong searching archived messages. Please try again.');
      setResults([]);
    } finally {
      if (searchSeq.current === mySeq) {
        setIsLoading(false);
        setHasSearched(true);
      }
    }
  }

  function selectedUserRoleRows(
    rows: UserRoleRow[]
  ): Array<{ id: string; role: UserRoleMatchRole }> {
    return rows
      .filter((row): row is UserRoleRow & { selected: ArchivingTypeaheadResult } => !!row.selected)
      .map((row) => ({ id: row.selected.id, role: row.role }));
  }

  async function onSubmit(event: React.FormEvent) {
    event.preventDefault();

    // A row with typed text but no picked suggestion would otherwise be silently dropped from the
    // filter below (the .filter(row => !!row.selected) keeps only picked rows) — that looks like the
    // user's search just ignored what they typed, so block the search and say why instead.
    const hasUnselectedTypedRow = userRoleRows.some(
      (row) => row.inputValue.trim().length > 0 && !row.selected
    );
    if (hasUnselectedTypedRow) {
      setError('Please pick a suggestion for each user/role you typed, or clear the field.');
      return;
    }

    await executeSearch({
      query,
      selectedRows: selectedUserRoleRows(userRoleRows),
      matchMode: userRoleMatchMode,
      messageType,
      hasAttachmentOnly,
      timeRangePreset,
      customStartDate,
      customEndDate,
    });
  }

  /** Resets every filter field back to its default, and clears any prior results/errors. */
  function onClear() {
    // Invalidate any in-flight search so its response can't land after clear and resurrect
    // stale results/errors.
    searchSeq.current += 1;
    setQuery('');
    setUserRoleRows([createRow()]);
    setUserRoleMatchMode('all');
    setMessageType('');
    setHasAttachmentOnly(false);
    setTimeRangePreset('last24h');
    setCustomStartDate(null);
    setCustomEndDate(null);
    setResults([]);
    setError(null);
    setHasSearched(false);
  }

  function buildCurrentFilters(): ArchivingSavedSearchFilters {
    const selectedRows = userRoleRows.filter((row) => !!row.selected);
    return {
      ...(query.trim() && { query: query.trim() }),
      timeRangePreset,
      ...(timeRangePreset === 'custom' &&
        customStartDate && { customStartDate: customStartDate.toISOString() }),
      ...(timeRangePreset === 'custom' &&
        customEndDate && { customEndDate: customEndDate.toISOString() }),
      userRoles: selectedRows.map((row) => ({
        // Guarded by the filter above, but TS can't narrow through Array#filter with a boolean predicate.
        id: row.selected?.id || '',
        displayName: row.selected?.displayName || '',
        type: row.selected?.type || 'user',
        role: row.role,
      })),
      userRoleMatchMode,
      ...(messageType && { messageType }),
      ...(hasAttachmentOnly && { hasAttachment: true }),
    };
  }

  async function onSaveSearch() {
    if (!client || !saveSearchName.trim()) return;

    // Same guard as onSubmit — a row with typed text but no picked suggestion would otherwise be
    // silently dropped from the saved filters (buildCurrentFilters only keeps picked rows).
    const hasUnselectedTypedRow = userRoleRows.some(
      (row) => row.inputValue.trim().length > 0 && !row.selected
    );
    if (hasUnselectedTypedRow) {
      setSaveSearchError(
        'Please pick a suggestion for each user/role you typed, or clear the field.'
      );
      return;
    }

    setSaveSearchError(null);
    try {
      await client.archiving.createSavedSearch(
        currentOrganizationId,
        saveSearchName.trim(),
        buildCurrentFilters()
      );
      setIsSaveSearchModalOpen(false);
      setSaveSearchName('');
    } catch (err) {
      Sentry.logger.error(Sentry.logger.fmt`[MessageSearch] Failed to save search`, { error: err });
      setSaveSearchError('Something went wrong saving this search. Please try again.');
    }
  }

  /** Restores every filter field from a saved search and immediately runs it. */
  function onRunSavedSearch(filters: ArchivingSavedSearchFilters) {
    const restoredPreset = (filters.timeRangePreset as TimeRangePreset) || 'last24h';
    const restoredStart = filters.customStartDate ? moment(filters.customStartDate) : null;
    const restoredEnd = filters.customEndDate ? moment(filters.customEndDate) : null;
    const restoredMatchMode = filters.userRoleMatchMode || 'all';
    const restoredMessageType = filters.messageType || '';
    const restoredHasAttachment = !!filters.hasAttachment;
    const restoredQuery = filters.query || '';
    const restoredRows: UserRoleRow[] =
      filters.userRoles && filters.userRoles.length > 0
        ? filters.userRoles.map((userRole) => ({
            ...createRow(),
            inputValue: userRole.displayName,
            selected: { id: userRole.id, displayName: userRole.displayName, type: userRole.type },
            role: userRole.role,
          }))
        : [createRow()];

    setQuery(restoredQuery);
    setTimeRangePreset(restoredPreset);
    setCustomStartDate(restoredStart);
    setCustomEndDate(restoredEnd);
    setUserRoleMatchMode(restoredMatchMode);
    setMessageType(restoredMessageType);
    setHasAttachmentOnly(restoredHasAttachment);
    setUserRoleRows(restoredRows);
    setError(null);
    setActiveTab('search');

    executeSearch({
      query: restoredQuery,
      selectedRows: selectedUserRoleRows(restoredRows),
      matchMode: restoredMatchMode,
      messageType: restoredMessageType,
      hasAttachmentOnly: restoredHasAttachment,
      timeRangePreset: restoredPreset,
      customStartDate: restoredStart,
      customEndDate: restoredEnd,
    });
  }

  const selectedResults = Array.from(selectedMessages.values());

  async function onGenerateExport() {
    if (!client) return;

    setIsExporting(true);
    setExportError(null);
    setExportJobId(null);
    try {
      const response = await client.archiving.startExport(currentOrganizationId, {
        ...(exportName && { exportName }),
        messageIds: Array.from(selectedMessages.keys()),
        includeAttachments,
        ...(exportNotes && { notes: exportNotes }),
      });
      setExportJobId(response?.data?.exportJobId || null);
    } catch (err) {
      setExportError('Something went wrong queuing the export. Please try again.');
    } finally {
      setIsExporting(false);
    }
  }

  return (
    <div className={classes()}>
      <div className={classes('sub-nav')} data-test-id="message-search-sub-nav">
        <button
          className={classes('sub-nav-item', { selected: activeTab === 'search' })}
          data-test-id="message-search-sub-nav-search"
          onClick={() => {
            setExportJobId(null);
            setActiveTab('search');
          }}
          type="button"
        >
          Message Search
        </button>
        <button
          className={classes('sub-nav-item', { selected: activeTab === 'savedSearches' })}
          data-test-id="message-search-sub-nav-saved-searches"
          onClick={() => setActiveTab('savedSearches')}
          type="button"
        >
          Saved Searches
        </button>
        <button
          className={classes('sub-nav-item', { selected: activeTab === 'exportBuilder' })}
          data-test-id="message-search-sub-nav-export-builder"
          onClick={() => setActiveTab('exportBuilder')}
          type="button"
        >
          Export Builder
        </button>
        <button
          className={classes('sub-nav-item', { selected: activeTab === 'exportHistory' })}
          data-test-id="message-search-sub-nav-export-history"
          onClick={() => {
            setExportJobId(null);
            setActiveTab('exportHistory');
          }}
          type="button"
        >
          Export History
        </button>
        {canViewArchivingAuditLog && (
          <button
            className={classes('sub-nav-item', { selected: activeTab === 'auditLog' })}
            data-test-id="message-search-sub-nav-audit-log"
            onClick={() => {
              setExportJobId(null);
              setActiveTab('auditLog');
            }}
            type="button"
          >
            Audit Log
          </button>
        )}
      </div>

      <div className={classes('content')}>
        {activeTab === 'savedSearches' ? (
          <SavedSearches
            client={client}
            currentOrganizationId={currentOrganizationId}
            currentUserId={client?.currentUserId || ''}
            onRun={onRunSavedSearch}
          />
        ) : activeTab === 'auditLog' && canViewArchivingAuditLog ? (
          <AuditLog client={client} currentOrganizationId={currentOrganizationId} />
        ) : activeTab === 'exportHistory' ? (
          <ExportHistory client={client} currentOrganizationId={currentOrganizationId} />
        ) : activeTab === 'exportBuilder' ? (
          <div className={classes('export-builder')} data-test-id="export-builder">
            <div className={classes('header')}>
              <div className={classes('header-text')}>
                <h2 className={classes('title')}>Export Builder</h2>
                <p className={classes('subtitle')}>
                  Review selected messages and configure an export package
                </p>
              </div>
            </div>

            <div className={classes('export-builder-panels')}>
              <div className={classes('export-builder-panel')}>
                <h3 className={classes('export-builder-panel-title')}>
                  Selected Messages ({selectedResults.length})
                </h3>
                {selectedResults.length === 0 ? (
                  <div className={classes('empty-state')}>
                    No messages selected. Select messages from Message Search to add them here.
                  </div>
                ) : (
                  <ul
                    className={classes('export-selected-list')}
                    data-test-id="export-builder-selected-list"
                  >
                    {selectedResults.map((result) => (
                      <li className={classes('export-selected-item')} key={result.messageId}>
                        <div className={classes('export-selected-item-meta')}>
                          <span className={classes('result-sender')}>
                            {result.senderDisplayName}
                          </span>
                          <span className={classes('result-date')}>
                            {result.sentAt && moment(result.sentAt).format('MMM D, YYYY h:mm A')}
                          </span>
                        </div>
                        <div className={classes('export-selected-item-body')}>{result.body}</div>
                        <button
                          aria-label="Remove message from export"
                          className={classes('export-selected-item-remove')}
                          data-test-id={`export-builder-remove-${result.messageId}`}
                          onClick={() => result.messageId && toggleSelected(result)}
                          type="button"
                        >
                          ✕
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div className={classes('export-builder-panel')}>
                <h3 className={classes('export-builder-panel-title')}>Export Configuration</h3>
                <label className={classes('export-config-field')}>
                  <span className={classes('export-config-label')}>
                    Export Name / Case Reference
                  </span>
                  <input
                    className={classes('export-config-input')}
                    data-test-id="export-builder-name"
                    onChange={(event) => setExportName(event.target.value)}
                    placeholder="e.g. HR Investigation #1042 – PHI Review"
                    type="text"
                    value={exportName}
                  />
                </label>

                <label className={classes('attachment-toggle')}>
                  <input
                    checked={includeAttachments}
                    data-test-id="export-builder-include-attachments"
                    onChange={(event) => setIncludeAttachments(event.target.checked)}
                    type="checkbox"
                  />
                  <span className={classes('attachment-toggle-label')}>Export attachments</span>
                </label>

                <label className={classes('export-config-field')}>
                  <span className={classes('export-config-label')}>Notes / Purpose</span>
                  <textarea
                    className={classes('export-config-textarea')}
                    data-test-id="export-builder-notes"
                    onChange={(event) => setExportNotes(event.target.value)}
                    placeholder="Describe the purpose of this export…"
                    value={exportNotes}
                  />
                </label>

                {exportError && <div className={classes('error')}>{exportError}</div>}
                {exportJobId && (
                  <div className={classes('export-success')} data-test-id="export-builder-success">
                    Export queued (job {exportJobId}). Check Export History for status.
                  </div>
                )}

                <button
                  className={classes('export-generate-button')}
                  data-test-id="export-builder-generate"
                  disabled={selectedResults.length === 0 || isExporting}
                  onClick={onGenerateExport}
                  type="button"
                >
                  {isExporting ? 'Generating…' : 'Generate Export Package'}
                </button>
              </div>
            </div>
          </div>
        ) : (
          <>
            <div className={classes('header')}>
              <div className={classes('header-text')}>
                <h2 className={classes('title')}>Message Search</h2>
                <p className={classes('subtitle')}>
                  Search archived messages for this organization
                </p>
              </div>
            </div>

            <form className={classes('search-bar')} onSubmit={onSubmit}>
              <input
                className={classes('search-input')}
                data-test-id="message-search-input"
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search archived messages…"
                type="text"
                value={query}
              />
              <button
                className={classes('search-button')}
                data-test-id="message-search-submit"
                disabled={isLoading}
                type="submit"
              >
                {isLoading ? 'Searching…' : 'Search'}
              </button>
              <button
                className={classes('clear-button')}
                data-test-id="message-search-clear"
                onClick={onClear}
                type="button"
              >
                Clear
              </button>
              <button
                className={classes('save-search-button')}
                data-test-id="message-search-save-search"
                onClick={() => {
                  setSaveSearchError(null);
                  setIsSaveSearchModalOpen(true);
                }}
                type="button"
              >
                Save Search
              </button>
            </form>

            <div className={classes('user-role-filters')}>
              <div className={classes('user-role-filters-header')}>
                <span className={classes('user-role-filters-label')}>Users</span>
                <div
                  className={classes('match-mode-toggle')}
                  data-test-id="message-search-match-mode"
                  role="group"
                >
                  <span className={classes('match-mode-label')}>Match</span>
                  <button
                    className={classes('match-mode-button', {
                      selected: userRoleMatchMode === 'any',
                    })}
                    data-test-id="message-search-match-mode-any"
                    onClick={() => setUserRoleMatchMode('any')}
                    type="button"
                  >
                    Any
                  </button>
                  <button
                    className={classes('match-mode-button', {
                      selected: userRoleMatchMode === 'all',
                    })}
                    data-test-id="message-search-match-mode-all"
                    onClick={() => setUserRoleMatchMode('all')}
                    type="button"
                  >
                    All
                  </button>
                </div>
              </div>

              {userRoleRows.map((row) => {
                const listboxId = `message-search-user-role-listbox-${row.id}`;
                const activeSuggestion = row.suggestions[row.activeSuggestionIndex];
                const activeOptionId = activeSuggestion
                  ? `message-search-user-role-option-${row.id}-${activeSuggestion.id}`
                  : undefined;
                return (
                  <div className={classes('user-role-row')} key={row.id}>
                    <div className={classes('user-role-row-input-wrapper')}>
                      <input
                        aria-activedescendant={activeOptionId}
                        aria-autocomplete="list"
                        aria-controls={listboxId}
                        aria-expanded={row.suggestions.length > 0}
                        className={classes('filter-input')}
                        data-test-id={`message-search-user-role-input-${row.id}`}
                        onBlur={() =>
                          updateRow(row.id, { suggestions: [], activeSuggestionIndex: -1 })
                        }
                        onChange={(event) => onRowInputChange(row.id, event.target.value)}
                        onKeyDown={(event) => onRowInputKeyDown(row, event)}
                        placeholder="Search by name…"
                        role="combobox"
                        type="text"
                        value={row.inputValue}
                      />
                      {row.isLoadingSuggestions && (
                        <div
                          className={classes('user-role-suggestions-status')}
                          data-test-id={`message-search-user-role-loading-${row.id}`}
                        >
                          Searching…
                        </div>
                      )}
                      {!row.isLoadingSuggestions && row.suggestionsError && (
                        <div
                          className={classes('user-role-suggestions-status', { error: true })}
                          data-test-id={`message-search-user-role-error-${row.id}`}
                        >
                          Something went wrong searching users/roles. Please try again.
                        </div>
                      )}
                      {!row.isLoadingSuggestions && row.suggestions.length > 0 && (
                        <ul
                          className={classes('user-role-suggestions')}
                          data-test-id={`message-search-user-role-suggestions-${row.id}`}
                          id={listboxId}
                          role="listbox"
                        >
                          {row.suggestions.map((suggestion, index) => (
                            <li key={suggestion.id}>
                              <button
                                aria-selected={index === row.activeSuggestionIndex}
                                className={classes('user-role-suggestion', {
                                  active: index === row.activeSuggestionIndex,
                                })}
                                data-test-id={`message-search-user-role-suggestion-${row.id}-${suggestion.id}`}
                                id={`message-search-user-role-option-${row.id}-${suggestion.id}`}
                                // Keeps focus (and thus the dropdown) on the input when a suggestion
                                // is clicked — without this, the mousedown-triggered blur would close
                                // the list before the click that selects it ever fires.
                                onMouseDown={(event) => event.preventDefault()}
                                onClick={() => onSelectSuggestion(row.id, suggestion)}
                                role="option"
                                type="button"
                              >
                                <span className={classes('user-role-suggestion-name')}>
                                  {suggestion.displayName}
                                </span>
                                <span className={classes('user-role-suggestion-type')}>
                                  {suggestion.type === 'role' ? 'Role' : 'User'}
                                </span>
                              </button>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>

                    <select
                      className={classes('user-role-select')}
                      data-test-id={`message-search-user-role-role-${row.id}`}
                      onChange={(event) =>
                        onRowRoleChange(row.id, event.target.value as UserRoleMatchRole)
                      }
                      value={row.role}
                    >
                      <option value="sender">as Sender</option>
                      <option value="recipient">as Recipient</option>
                      <option value="either">as Either</option>
                    </select>

                    <button
                      aria-label="Remove row"
                      className={classes('user-role-row-remove')}
                      data-test-id={`message-search-user-role-remove-${row.id}`}
                      onClick={() => removeUserRoleRow(row.id)}
                      type="button"
                    >
                      ✕
                    </button>
                  </div>
                );
              })}

              <button
                className={classes('add-user-role-row')}
                data-test-id="message-search-add-user-role-row"
                onClick={addUserRoleRow}
                type="button"
              >
                + Add User / Role
              </button>
            </div>

            <div className={classes('filters')}>
              <select
                className={classes('filter-input')}
                data-test-id="message-search-message-type"
                onChange={(event) => setMessageType(event.target.value)}
                value={messageType}
              >
                <option value="">All Types</option>
                {MESSAGE_TYPE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <label className={classes('attachment-toggle')}>
                <input
                  checked={hasAttachmentOnly}
                  data-test-id="message-search-has-attachment-toggle"
                  onChange={(event) => setHasAttachmentOnly(event.target.checked)}
                  type="checkbox"
                />
                <span className={classes('attachment-toggle-label')}>Has attachment only</span>
              </label>
            </div>

            <div className={classes('time-range')}>
              <span className={classes('time-range-label')}>Time range</span>
              <div
                className={classes('time-range-chips')}
                data-test-id="message-search-time-range"
                role="group"
              >
                {TIME_RANGE_PRESETS.map((preset) => (
                  <button
                    className={classes('time-range-chip', {
                      selected: timeRangePreset === preset.value,
                    })}
                    data-test-id={`message-search-time-range-${preset.value}`}
                    key={preset.value}
                    onClick={() => setTimeRangePreset(preset.value)}
                    type="button"
                  >
                    {preset.label}
                  </button>
                ))}
              </div>

              {timeRangePreset === 'custom' && (
                <div className={classes('custom-range')}>
                  <SingleDatePicker
                    date={customStartDate}
                    displayFormat="MM/DD/YYYY"
                    focused={customStartFocused}
                    hideKeyboardShortcutsPanel
                    id="message-search-custom-start-date"
                    isOutsideRange={(day: Moment) =>
                      customEndDate != null && day.isAfter(customEndDate)
                    }
                    numberOfMonths={1}
                    onDateChange={setCustomStartDate}
                    onFocusChange={({ focused }: { focused: boolean }) =>
                      setCustomStartFocused(focused)
                    }
                    placeholder="From"
                  />
                  <SingleDatePicker
                    date={customEndDate}
                    displayFormat="MM/DD/YYYY"
                    focused={customEndFocused}
                    hideKeyboardShortcutsPanel
                    id="message-search-custom-end-date"
                    isOutsideRange={(day: Moment) =>
                      customStartDate != null && day.isBefore(customStartDate)
                    }
                    numberOfMonths={1}
                    onDateChange={setCustomEndDate}
                    onFocusChange={({ focused }: { focused: boolean }) =>
                      setCustomEndFocused(focused)
                    }
                    placeholder="To"
                  />
                </div>
              )}
            </div>

            {error && <div className={classes('error')}>{error}</div>}
            {addThreadError && <div className={classes('error')}>{addThreadError}</div>}

            {!error && hasSearched && !isLoading && results.length === 0 && (
              <div className={classes('empty-state')}>No messages found.</div>
            )}

            {results.length > 0 && (
              <div
                className={classes('selection-header')}
                data-test-id="message-search-selection-header"
              >
                <span className={classes('selection-header-count')}>
                  {selectedMessageIds.size > 0 ? `${selectedMessageIds.size} selected` : ''}
                </span>
                <div className={classes('selection-header-actions')}>
                  <button
                    className={classes('selection-header-button')}
                    data-test-id="message-search-export-all-results"
                    onClick={() => {
                      selectAllResults();
                      setActiveTab('exportBuilder');
                    }}
                    type="button"
                  >
                    Export All Results ({results.length})
                  </button>
                  {selectedMessageIds.size > 0 && (
                    <>
                      <button
                        className={classes('selection-header-button')}
                        data-test-id="message-search-add-selected-to-export"
                        onClick={() => setActiveTab('exportBuilder')}
                        type="button"
                      >
                        Add Selected to Export
                      </button>
                      <button
                        className={classes('selection-header-clear')}
                        data-test-id="message-search-clear-selection"
                        onClick={clearSelection}
                        type="button"
                      >
                        ✕ Clear
                      </button>
                    </>
                  )}
                </div>
              </div>
            )}

            {results.length > 0 && (
              <ul className={classes('results')} data-test-id="message-search-results">
                {results.map((result, index) => {
                  const resultKey = result.messageId || String(index);
                  return (
                    <li className={classes('result')} key={resultKey}>
                      <input
                        aria-label="Select message for export"
                        checked={!!result.messageId && selectedMessageIds.has(result.messageId)}
                        className={classes('result-checkbox')}
                        data-test-id={`message-search-result-checkbox-${resultKey}`}
                        disabled={!result.messageId}
                        onChange={() => result.messageId && toggleSelected(result)}
                        type="checkbox"
                      />
                      <div className={classes('result-content')}>
                        <div className={classes('result-meta')}>
                          <span className={classes('result-sender')}>
                            {result.senderDisplayName}
                          </span>
                          {result.recipientAccountId ? (
                            <span className={classes('result-recipient')}>
                              to {result.recipientDisplayName || result.recipientAccountId}
                            </span>
                          ) : (
                            result.channelId && (
                              <span className={classes('result-recipient')}>
                                to multiple recipients
                              </span>
                            )
                          )}
                          <span className={classes('result-date')}>
                            {result.sentAt && moment(result.sentAt).format('MMM D, YYYY h:mm A')}
                          </span>
                          {result.messageType && (
                            <span
                              className={classes('result-message-type')}
                              data-test-id="message-search-result-message-type"
                            >
                              {result.messageType}
                            </span>
                          )}
                        </div>
                        <div className={classes('result-body')}>{result.body}</div>
                        <div className={classes('result-actions')}>
                          <button
                            className={classes('result-action')}
                            data-test-id={`message-search-view-thread-${resultKey}`}
                            disabled={!result.conversationId}
                            onClick={() =>
                              result.conversationId &&
                              setOpenThread({
                                conversationId: result.conversationId,
                                messageId: result.messageId,
                              })
                            }
                            type="button"
                          >
                            View Thread
                          </button>
                          <button
                            className={classes('result-action')}
                            data-test-id={`message-search-add-to-export-${resultKey}`}
                            disabled={!result.messageId || selectedMessageIds.has(result.messageId)}
                            onClick={() => result.messageId && toggleSelected(result)}
                            type="button"
                          >
                            {result.messageId && selectedMessageIds.has(result.messageId)
                              ? 'Added to Export'
                              : 'Add to Export'}
                          </button>
                          <button
                            className={classes('result-action')}
                            data-test-id={`message-search-add-full-thread-${resultKey}`}
                            disabled={
                              !result.conversationId || addingThreadId === result.conversationId
                            }
                            onClick={() => onAddFullThread(result.conversationId)}
                            type="button"
                          >
                            {addingThreadId === result.conversationId
                              ? 'Adding…'
                              : 'Add Full Thread'}
                          </button>
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}

            {openThread && (
              <ConversationThreadPanel
                client={client}
                conversationId={openThread.conversationId}
                currentOrganizationId={currentOrganizationId}
                focusedMessageId={openThread.messageId}
                onAddToExport={addToSelection}
                onClose={() => setOpenThread(null)}
                selectedMessageIds={selectedMessageIds}
              />
            )}
          </>
        )}
      </div>

      <BasicModal
        ariaLabelBody="save-search-modal-body"
        ariaLabelCancelButton="save-search-modal-cancel"
        ariaLabelCloseButton="save-search-modal-close"
        ariaLabelHeader="save-search-modal-header"
        ariaLabelSubmitButton="save-search-modal-submit"
        headerText="Save Search"
        isOpen={isSaveSearchModalOpen}
        onClose={() => {
          setIsSaveSearchModalOpen(false);
          setSaveSearchName('');
          setSaveSearchError(null);
        }}
        onSubmit={onSaveSearch}
        submitDisabled={!saveSearchName.trim()}
        submitText="Save"
      >
        <label className={classes('save-search-name-field')}>
          <span className={classes('save-search-name-label')}>Name</span>
          <input
            className={classes('save-search-name-input')}
            data-test-id="save-search-name-input"
            onChange={(event) => setSaveSearchName(event.target.value)}
            placeholder="Search name"
            type="text"
            value={saveSearchName}
          />
        </label>
        {saveSearchError && <div className={classes('error')}>{saveSearchError}</div>}
      </BasicModal>
    </div>
  );
}

export default mobxInjectSelect<MessageSearchProps, MobxProps>({
  messengerStore: ['client', 'currentOrganizationId', 'canViewArchivingAuditLog'],
})(MessageSearchInner);
